"use client"
import type React from "react"
import { useEffect, useState } from "react"
import { ChevronDown, BarChart3, Users, FileText, Target } from "lucide-react"
import UserTemplatesView from "./questionnaire/views/UserTemplatesView"
import UserLeadershipView from "./questionnaire/views/UserLeadershipView"
import UserValuesView from "./questionnaire/views/UserValueView"
import UserRequests from "./requests/UserRequests"
import Resubmissions from "./requests/Resubmissions"
import Reports from "./ratings/Reports"
import { cn } from "../shared/utils/utils"
import { Button } from "../dashboard/company-dashboard/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dashboard/company-dashboard/ui/dropdown-menu"
import { UserDashboard } from "./dashboard/UsersDashboard"
import RatingsOverview from "./ratings/RatingsOverview"

interface TabButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
  icon?: React.ReactNode
  dropdownItems?: { label: string; onClick: () => void }[]
}

const TabButton: React.FC<TabButtonProps> = ({ label, isActive, onClick, icon, dropdownItems }) => {
  if (dropdownItems && dropdownItems.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl transition-all duration-200 border-2 border-transparent hover:scale-105",
              isActive
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-200",
            )}
          >
            {icon && <span className="text-sm sm:text-lg">{icon}</span>}
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{label.split(" ")[0]}</span>
            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-48 sm:w-56 p-2 bg-white/95 backdrop-blur-sm border border-slate-200 shadow-xl rounded-xl"
        >
          {dropdownItems.map((item, index) => (
            <DropdownMenuItem
              key={index}
              onClick={item.onClick}
              className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors text-sm"
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl transition-all duration-200 border-2 border-transparent hover:scale-105",
        isActive
          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-200",
      )}
    >
      {icon && <span className="text-sm sm:text-lg">{icon}</span>}
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{label.split(" ")[0]}</span>
    </Button>
  )
}

export const Main360 = () => {
  const [activeTab, setActiveTab] = useState<string>(() => {
    return typeof window !== "undefined" ? localStorage.getItem("activeTab") || "dashboard" : "dashboard"
  })

  const handleTab = (label: string) => {
    setActiveTab(label)
    if (typeof window !== "undefined") {
      localStorage.setItem("activeTab", label)
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("activeTab", activeTab)
    }
  }, [activeTab])

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header - Full Width */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1 sm:mb-2">
          360° Feedback Platform
        </h1>
        <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
          Comprehensive performance insights and development tracking
        </p>
      </div>

      {/* Main Content Card - Full Width */}
      <div className="w-full bg-white/80 backdrop-blur-sm shadow-xl border-t border-white/20">
        {/* Navigation Tabs - Full Width */}
        <div className="w-full bg-white/50 border-b border-slate-200/50 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <TabButton
              label="Dashboard"
              isActive={activeTab === "dashboard"}
              onClick={() => handleTab("dashboard")}
              icon={<BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />}
            />
            <TabButton
              label="My Ratings"
              isActive={activeTab.startsWith("Ratings")}
              onClick={() => {}}
              icon={<Target className="h-3 w-3 sm:h-4 sm:w-4" />}
              dropdownItems={[
                { label: "📊 Analysis", onClick: () => handleTab("Ratings-analysis") },
                { label: "📋 Reports", onClick: () => handleTab("Ratings-reports") },
              ]}
            />
            <TabButton
              label="Surveys"
              isActive={activeTab.startsWith("surveys")}
              onClick={() => {}}
              icon={<Users className="h-3 w-3 sm:h-4 sm:w-4" />}
              dropdownItems={[
                { label: "💎 Values", onClick: () => handleTab("surveys-values") },
                { label: "👑 Leadership", onClick: () => handleTab("surveys-leadership") },
                { label: "🎯 Professional Competencies", onClick: () => handleTab("surveys-requests") },
                { label: "🔄 Resubmissions", onClick: () => handleTab("surveys-resubmissions") },
              ]}
            />
            <TabButton
              label="Requests"
              isActive={activeTab === "requests"}
              onClick={() => handleTab("requests")}
              icon={<FileText className="h-3 w-3 sm:h-4 sm:w-4" />}
            />
          </div>
        </div>

        {/* Content Area - Full Width */}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          {activeTab === "dashboard" && <UserDashboard />}
          {activeTab === "surveys-values" && <UserValuesView />}
          {activeTab === "surveys-leadership" && <UserLeadershipView />}
          {activeTab === "surveys-requests" && <UserRequests />}
          {activeTab === "surveys-resubmissions" && <Resubmissions />}
          {activeTab === "Ratings-analysis" && <RatingsOverview />}
          {activeTab === "Ratings-reports" && <Reports />}
          {activeTab === "requests" && <UserTemplatesView />}
        </div>
      </div>
    </div>
  )
}

export default Main360
